import React from "react";
import { Grid, Typography, Alert, AlertTitle } from "@mui/material";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import { ErrorBoundary } from "react-error-boundary";
import { logger } from "@caju-beneficios/monitoring";

import { FormWrapper } from "../../../components/formWrapper";
import { Tokens } from "../../../configs/tokens/tokens";
import { InfoCard } from "./infoCard";
import { WarningCard } from "./warningCard";
import { getCalendlyLink } from "./utils";
import { QueuedUpdateContext } from "../queuedUpdateContext/context";
import { TagManagerService } from "services/tagManager";
import { EnumGTMEvents } from "services/tagManager/enums/enumGTMEvents";
import { IStep3FormValues } from "../step3/useStep3Form/iStep3FormValues";
import { calculateProductMappings } from "../step3/productsList/productMapping";
import { Solutions } from "../step3/productsList/productConstants";

function CalendlyErrorFallback() {
  return (
    <Alert severity="error">
      <AlertTitle>Erro ao carregar agendamento</AlertTitle>
      Não foi possível carregar o sistema de agendamento. Por favor, tente
      novamente mais tarde ou entre em contato conosco.
    </Alert>
  );
}

interface Step5Props {
  name?: string;
  email?: string;
  employeesNumber?: number | "";
  step3Values?: IStep3FormValues;
  externalId: string;
  onGoNext: () => void;
}

export function Step5(props: Step5Props) {
  const [openInfoCard, setOpenInfoCard] = React.useState(false);
  const [openWarningCard, setOpenWarningCard] = React.useState(false);
  const [dateAndTimeSelected, setdateAndTimeSelected] = React.useState(false);

  const queueContext = React.useContext(QueuedUpdateContext);

  useCalendlyEventListener({
    onDateAndTimeSelected: () => {
      setdateAndTimeSelected(true);
      TagManagerService.push(
        EnumGTMEvents.MEETING_INFO_DATE_OPTION_DATE__CLICK
      );
    },
    onEventTypeViewed: () => {
      setdateAndTimeSelected(false);
    },
    onEventScheduled: () => {
      TagManagerService.push(
        EnumGTMEvents.MEETING_INFO_SCHEDULING_CONFIRMATION_SUCCESS__DATA_SENT
      );
      if (queueContext.error) {
        TagManagerService.push(EnumGTMEvents.ERROR_MSG__VIEW);
      }
      handleBookedMetting();
    },
  });

  const { solutions, subscriptions } = calculateProductMappings(
    props.step3Values?.selectedProducts || []
  );
  const itsOnlyAwards =
    solutions.length === 1 &&
    solutions[0] === Solutions.AWARDS &&
    subscriptions.length === 0;

  const styles = {
    height: "750px",
  };

  const handleBookedMetting = () => {
    props.onGoNext();
  };

  const pageSettings = {
    backgroundColor: Tokens.neutralColor.light["1000"],
    primaryColor: Tokens.interfaceColor.primary.pure,
    textColor: Tokens.neutralColor.dark["200"],
    minHeight: "auto",
    overflowY: "hidden",
    hideEventTypeDetails: true,
    hideLandingPageDetails: true,
  };

  const hasLessThan10Employees =
    props?.employeesNumber && props?.employeesNumber < 10;

  const hasWellhubProduct =
    subscriptions.some((product: string) => product?.includes("Wellhub")) ||
    false;
  const dontAllowWellhubInCajuMais =
    hasLessThan10Employees && hasWellhubProduct;

  const calendlyLink =
    (getCalendlyLink(props?.step3Values, props?.employeesNumber) as unknown) ||
    "";

  return (
    <FormWrapper>
      <Grid
        item
        container
        xs={12}
        direction={{ xs: "column-reverse", lg: "row" }}
      >
        <Grid
          item
          xs={12}
          lg={dateAndTimeSelected ? 12 : 7}
          sx={{ mt: { xs: 2, lg: 0 } }}
        >
          {calendlyLink && (
            <ErrorBoundary
              fallback={<CalendlyErrorFallback />}
              onError={(error, errorInfo) => {
                logger.error({
                  message: `Calendly widget error: ${error.message}`,
                  error,
                  context: {
                    application: "quero-caju",
                    component: "Step5-CalendlyWidget",
                    calendlyLink,
                    componentStack: errorInfo.componentStack,
                  },
                });
              }}
            >
              <InlineWidget
                url={calendlyLink as string}
                prefill={{ name: props?.name, email: props?.email }}
                pageSettings={pageSettings}
                styles={styles}
              />
            </ErrorBoundary>
          )}
        </Grid>
        <Grid item xs={0} lg={1}></Grid>
        <Grid
          item
          xs={12}
          lg={4}
          display={dateAndTimeSelected ? "none" : "block"}
        >
          {dontAllowWellhubInCajuMais && (
            <WarningCard
              title="Importante"
              isAccordion={true}
              open={openWarningCard}
              setOpen={setOpenWarningCard}
            >
              <>
                <Typography
                  variant="body1"
                  color={Tokens.feedbackColor.warning.dark}
                >
                  No momento a oferta de Wellhub está disponível para empresas a
                  partir de <strong>10 colaboradores</strong>.
                </Typography>
                <Typography
                  variant="body1"
                  color={Tokens.feedbackColor.warning.dark}
                >
                  Não se preocupe, você pode seguir conhecendo Caju Mais com os
                  serviços da Conexa Saúde, Optum e SAT Ivoor.
                </Typography>
              </>
            </WarningCard>
          )}
          {props.employeesNumber && props.employeesNumber < 20 ? (
            <InfoCard
              title="Mesa Aberta com a Caju"
              subheader="Sobre a reunião"
              open={openInfoCard}
              setOpen={setOpenInfoCard}
            >
              <>
                <Typography variant="body2" color="dark.light">
                  O evento funciona como uma roda de conversa, onde um de nossos
                  especialistas fica a disposição para te apresentar a
                  plataforma e contar um pouquinho mais sobre a Caju.
                </Typography>
                <Typography variant="body2" color="dark.light">
                  Vamos falar sobre todos os nossos produtos e apresentar a
                  plataforma do RH.
                </Typography>
                <Typography variant="body2" color="dark.light">
                  Aproveite para tirar todas as suas dúvidas antes de começar a
                  usar Caju!
                </Typography>
              </>
            </InfoCard>
          ) : (
            <InfoCard
              title="Introdução Caju"
              subheader="Sobre a reunião"
              open={openInfoCard}
              setOpen={setOpenInfoCard}
            >
              <>
                <Typography variant="body2" color="dark.light">
                  Esse será um papo com uma pessoa do nosso time de vendas para
                  entender suas necessidades e direcionar sua experiência da
                  melhor forma
                </Typography>
                <Typography variant="body2" color="dark.light">
                  Aproveite para tirar todas as suas dúvidas antes de começar a
                  usar Caju!
                </Typography>
              </>
            </InfoCard>
          )}

          {itsOnlyAwards && (
            <WarningCard
              title="Importante"
              isAccordion={true}
              subheader="Sobre custos para premiar"
              open={openWarningCard}
              setOpen={setOpenWarningCard}
            >
              <>
                <Typography
                  variant="body2"
                  color={Tokens.feedbackColor.warning.dark}
                >
                  Oferecemos cartões virtuais gratuitamente, uma opção
                  conveniente e ágil.
                </Typography>
                <Typography
                  variant="body2"
                  color={Tokens.feedbackColor.warning.dark}
                >
                  Você pode adquirir cartões físicos para seus colaboradores
                  após realizar seu cadastro na plataforma.
                </Typography>
                <Typography
                  variant="body2"
                  color={Tokens.feedbackColor.warning.dark}
                >
                  Se sua empresa contratar multi benefícios além de premiações,
                  a emissão e envio de cartões são gratuitos.
                </Typography>
                <Typography
                  variant="body2"
                  color={Tokens.feedbackColor.warning.dark}
                >
                  Para isso, volte à página anterior e selecione{" "}
                  <strong>também</strong> a opção "Caju Multibenefícios".
                </Typography>
              </>
            </WarningCard>
          )}
        </Grid>
      </Grid>
    </FormWrapper>
  );
}
